import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { MediaType } from '@awesome-cordova-plugins/camera/ngx';
import {
    CapacitorPluginService,
    HORSE_MATRIX_INFO,
    HorseEvaluationType,
    HorseMatrixSimple,
    HorseMatrixType,
    HorseService,
    Media,
    MediaData,
    MediaDocumentType,
    MediaSelectionSource,
    ModalService,
    bytesToMB
} from '@caballus/ui-common';
import { Directory, DownloadFileOptions, Filesystem } from '@capacitor/filesystem';
import { CameraDirection, CameraResultType, CameraSource, ImageOptions } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { MediaCache } from '@ion-caballus/core/caches';
import { ModalService as CoreModalService } from '@ion-caballus/core/services';
import { ModalController } from '@ionic/angular';
import { createObjectID } from 'mongo-object-reader';
import { Options } from 'ngx-slider-v2';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, forkJoin, iif, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { Browser } from '@capacitor/browser';

const INVALID_FILE_WARNING =
    'Allowed filesized (50Mb) exceeded or an incorrect format and will not be uploaded. Upload failed';

const MAX_ATTACHMENT_WARNING = 'Allowed maximum 2 attachments';
const TARGET_BLANK = '_blank';
const MAX_FILE_SIZE_IN_MB = 50980;
const MAXIMUM_FILES = 5;
const MAXIMUM_NOTES_LENGTH = 250;
const MAX_FILE_NAME_LENGTH = 14;
const ACCEPTS_FILE_TYPES = [
    '.jpg',
    '.jpeg',
    '.png',
    '.pdf',
    '.doc',
    '.docx',
    '.pages',
    '.epub',
    '.txt',
    '.rtf',
    '.zip',
    '.mov',
    '.mp4'
];
const SLIDER_OPTIONS: Options = {
    showTicksValues: true,
    stepsArray: [
        { value: 0 },
        { value: 1 },
        { value: 2 },
        { value: 3 },
        { value: 4 },
        { value: 5 },
        { value: 6 },
        { value: 7 },
        { value: 8 },
        { value: 9 },
        { value: 10 }
    ]
};
const READ_ONLY_SLIDER_OPTIONS: Options = { ...SLIDER_OPTIONS, readOnly: true };
const DEFAULT_RATING = 5;

@Component({
    selector: 'app-horse-matrix-modal',
    templateUrl: './horse-matrix-modal.component.html',
    styleUrls: ['./horse-matrix-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorseMatrixModalComponent implements OnInit {
    public notesLength: number = MAXIMUM_NOTES_LENGTH;
    @Input()
    public horseMatrix!: HorseMatrixSimple;
    @Input()
    public evaluationType!: HorseEvaluationType;
    @Input()
    public isPastEditEvaluationDeadline$!: boolean;
    public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public horseMatrixForm: FormGroup = this._formBuilder.group({
        horseMatrixType: ['', Validators.required],
        rating: [DEFAULT_RATING],
        notes: ['', Validators.maxLength(MAXIMUM_NOTES_LENGTH)]
    });
    public validations = {
        notes: [
            {
                type: 'maxlength',
                message: `Notes cannot be more than ${MAXIMUM_NOTES_LENGTH} characters long.`
            }
        ]
        // other validations
    };

    public loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public newDocuments$: BehaviorSubject<MediaData[]> = new BehaviorSubject([]);
    public existingDocuments$: BehaviorSubject<Media[]> = new BehaviorSubject([]);

    public sliderOptions: Options = SLIDER_OPTIONS;
    public readonlySliderOptions: Options = READ_ONLY_SLIDER_OPTIONS;
    public readonly HorseMatrixType: typeof HorseMatrixType = HorseMatrixType;
    public readonly horseMatrixInfo: typeof HORSE_MATRIX_INFO = HORSE_MATRIX_INFO;
    private _isUploadFormDirty$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public existingDocumentsDeleting$: BehaviorSubject<Record<string, boolean>> =
        new BehaviorSubject({});
    public selectedImage$: BehaviorSubject<string | null> = new BehaviorSubject(null);
    public readonly HorseEvaluationType: typeof HorseEvaluationType = HorseEvaluationType;
    public isLocked$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private readonly _modalController: ModalController,
        private readonly _horseService: HorseService,
        private readonly _toastService: ToastrService,
        private readonly _formBuilder: FormBuilder,
        private readonly _modalService: ModalService,
        private readonly _coreModalService: CoreModalService,
        private readonly _capacitorPluginService: CapacitorPluginService,
        private readonly _mediaCache: MediaCache,
        private readonly _sanitizer: DomSanitizer
    ) {}

    public ngOnInit(): void {
        this.isLocked$.next(this.isPastEditEvaluationDeadline$);

        this.horseMatrixForm.patchValue({
            horseMatrixType: this.horseMatrix.horseMatrixType,
            rating: this.horseMatrix.rating || DEFAULT_RATING,
            notes: this.horseMatrix.notes || ''
        });
        if (this.horseMatrix.documents?.length) {
            this.existingDocuments$.next(
                this.horseMatrix.documents.map(({ latest, ...rest }) => ({
                    ...rest,
                    latest,
                    displayName: this.displayFileName(latest.name)
                }))
            );
        }
        if (this.horseMatrix.newDocuments?.length) {
            this.newDocuments$.next(this.horseMatrix.newDocuments);
        }
    }

    public attachMedia(): void {
        this._capacitorPluginService
            .selectMediaToUpload()
            .pipe(
                switchMap(source => {
                    switch (source) {
                        // Get from gallery
                        case MediaSelectionSource.PICK_PHOTO:
                            return this._handlePhotoAttachment(MediaSelectionSource.PICK_PHOTO);
                        // Get from camera
                        case MediaSelectionSource.TAKE_PHOTO:
                            return this._handlePhotoAttachment(MediaSelectionSource.TAKE_PHOTO);
                        // Get from gallery
                        case MediaSelectionSource.PICK_VIDEO:
                            return this._handleVideoAttachment();
                        case MediaSelectionSource.PICK_DOCUMENT:
                            return this._handleDocumentsAttachment();
                        default:
                            break;
                    }
                })
            )
            .subscribe({
                next: media => {
                    this.loading$.next(false);
                    if (media) {
                        this.newDocuments$.next([...this.newDocuments$.getValue(), media]);
                    }
                }
            });
    }

    public onSave(): void {
        if (
            this.horseMatrixForm.get('notes').errors &&
            this.horseMatrixForm.get('notes').errors.maxlength &&
            this.horseMatrixForm.invalid
        ) {
            this._toastService.error('Not all required fields have been completed');
            return;
        }
        forkJoin([this.newDocuments$.pipe(take(1)), this.existingDocuments$.pipe(take(1))])
            .pipe(
                take(1),
                tap(([documents, existingDocuments]) => {
                    this._modalController.dismiss({
                        saved: true,
                        horseId: this.horseMatrix?.horseIdentity?._id,
                        horseMatrixType: this.horseMatrixForm.value.horseMatrixType,
                        notes: this.horseMatrixForm.value.notes,
                        rating: this.horseMatrixForm.value.rating,
                        documents,
                        existingDocuments
                    });
                })
            )
            .subscribe();
    }

    public goBack(): void {
        if (this.horseMatrixForm.dirty || this._isUploadFormDirty$.getValue()) {
            this._modalService
                .openActionDialog(
                    'Save Changes',
                    'There have been changes to this item.  Do you wish to save those changes?',
                    'Save',
                    'Discard'
                )
                .afterClosed()
                .pipe(
                    take(1),
                    tap(button => {
                        if (button && button === 'Button1') {
                            this.onSave();
                        } else {
                            this._modalController.dismiss({ saved: false, deleted: false });
                        }
                    })
                )
                .subscribe();
        } else {
            this._modalController.dismiss({ saved: false, deleted: false });
        }
    }

    private _isValidFiles(files): boolean {
        if (
            this.existingDocuments$.getValue().length +
                this.newDocuments$.getValue().length +
                files.length >
            MAXIMUM_FILES
        ) {
            this._toastService.error(MAX_ATTACHMENT_WARNING);
            return false;
        }
        const validFileTypes = files.every(file => {
            const splits = file.name.split('.');
            let ext = splits[splits.length - 1];
            if (typeof ext === 'undefined') {
                ext = '';
            }
            if (!ACCEPTS_FILE_TYPES.includes('.' + ext)) {
                return false;
            }
            if (bytesToMB(file.size) > MAX_FILE_SIZE_IN_MB) {
                return false;
            }
            return true;
        });
        if (!validFileTypes) {
            this._toastService.error(INVALID_FILE_WARNING);
            return false;
        }
        return validFileTypes;
    }

    public displayFileName(fileName: string): string {
        let displayName = fileName;
        if (fileName.length > MAX_FILE_NAME_LENGTH) {
            const fileNameArr = fileName.split('.');
            const ext = fileNameArr.pop();
            displayName = fileNameArr.join('.').substr(0, MAX_FILE_NAME_LENGTH) + '...' + ext;
        }
        return displayName;
    }

    public deleteNewMedia(deletedIndex: number, fileName: string): void {
        this._modalService
            .openActionDialog(
                'Remove File',
                `Do you want to remove this attachment ${fileName}?`,
                'Remove',
                'Keep'
            )
            .afterClosed()
            .pipe(
                switchMap(button =>
                    iif(
                        () => button && button === 'Button1',
                        this.newDocuments$.pipe(take(1)),
                        of(null)
                    )
                ),
                tap((documents: MediaData[] | null) => {
                    if (documents) {
                        documents.splice(deletedIndex, 1);
                        this.newDocuments$.next(documents);
                    }
                })
            )
            .subscribe();
    }

    public deleteExistingDocument(id: string, fileName: string, index: number): void {
        this.existingDocumentsDeleting$.next({
            [index]: true
        });
        this._modalService
            .openActionDialog(
                'Remove File',
                `Do you want to remove this attachment ${fileName}?`,
                'Remove',
                'Keep'
            )
            .afterClosed()
            .pipe(
                switchMap(button =>
                    iif(
                        () => button && button === 'Button1',
                        this._horseService
                            .deleteHorseHealthDocument(id, this.horseMatrix?.horseIdentity?._id)
                            .pipe(take(1)),
                        of(true)
                    )
                ),
                tap(isDiscard => {
                    if (!isDiscard) {
                        this.existingDocuments$.next(
                            this.existingDocuments$.getValue().filter(({ _id }) => _id !== id)
                        );
                        this.isLoading$.next(false);
                    }
                    this.existingDocumentsDeleting$.next({});
                }),
                catchError(() => {
                    this._toastService.error('Error deleting horse health document');
                    return of(null);
                }),
                finalize(() => this.isLoading$.next(false))
            )
            .subscribe();
    }

    public setPreviewImage(imgUrl: string | null): void {
        this.selectedImage$.next(imgUrl);
    }

    public previewExistingFile(doc: Media): void {
        if (doc.latest.type === MediaDocumentType.Image) {
            this._imageViewerModal(doc.latest.url, doc._id);
        } else if (doc.latest.type === MediaDocumentType.Video) {
            this._videoViewerModal(doc.latest.jwPlayerId);
        } else if (doc.latest.type === MediaDocumentType.Document) {
            Browser.open({ url: doc.latest.url })
                .then(() => {
                })
                .catch(err => {
                });
        }
    }

    public _imageViewerModal(attachmentUrl: string, mediaId: string): void {
        if (!attachmentUrl) {
            return;
        }
        this._coreModalService
            .imageViewerModal(attachmentUrl, mediaId, null, false, true, MediaType.PICTURE)
            .subscribe();
    }

    public _videoViewerModal(jwPlayerId): void {
        this._coreModalService.videoViewerModal(jwPlayerId).subscribe();
    }

    private _handlePhotoAttachment(source: MediaSelectionSource): Observable<MediaData> {
        this.loading$.next(true);

        const imageOptions: ImageOptions = {
            source:
                source === MediaSelectionSource.PICK_PHOTO
                    ? CameraSource.Photos
                    : CameraSource.Camera,
            resultType: CameraResultType.DataUrl,
            allowEditing: false,
            saveToGallery: source === MediaSelectionSource.TAKE_PHOTO,
            ...(source === MediaSelectionSource.TAKE_PHOTO && {
                direction: CameraDirection.Rear,
                correctOrientation: true
            })
        };

        return this._mediaCache
            .preparePhotoData(
                null, // This will be taken from the horse evaluation matrix when it is created
                imageOptions
            )
            .pipe(
                catchError(err => {
                    this.loading$.next(false);
                    this._toastService.error('Add Photo failed');
                    throw err;
                })
            );
    }

    private _handleVideoAttachment(): Observable<MediaData> {
        this.loading$.next(true);

        return this._capacitorPluginService.getVideoAttachment().pipe(
            filter(res => !!res),
            switchMap(res =>
                this._mediaCache.prepareVideoData({
                    mediaSubjectId: null, // This will be taken from the horse evaluation matrix when it is created
                    mediaId: createObjectID(),
                    dataUrl: Capacitor.convertFileSrc(res.originalFilePath),
                    thumbnailDataUrl: res.thumbnail,
                    mediaType: MediaDocumentType.Video,
                    safeUrl: this._sanitizer.bypassSecurityTrustUrl(
                        Capacitor.convertFileSrc(res.originalFilePath)
                    ),
                    filePath: res.originalFilePath,
                    fileSize: res.fileSize,
                    fileName: res.fileName
                })
            ),
            catchError(err => {
                this.loading$.next(false);
                this._toastService.error('Add Video failed');
                throw err;
            })
        );
    }

    private _handleDocumentsAttachment(): Observable<MediaData> {
        this.loading$.next(true);

        return this._capacitorPluginService.getDocumentAttachment().pipe(
            filter(res => !!res),
            switchMap(res =>
                this._mediaCache.prepareDocumentData({
                    mediaSubjectId: null, // This will be taken from the horse evaluation matrix when it is created
                    mediaId: createObjectID(),
                    dataUrl: Capacitor.convertFileSrc(res.originalFilePath),
                    thumbnailDataUrl: '',
                    mediaType: MediaDocumentType.Document,
                    safeUrl: this._sanitizer.bypassSecurityTrustUrl(
                        Capacitor.convertFileSrc(res.originalFilePath)
                    ),
                    filePath: res.originalFilePath,
                    fileSize: res.fileSize,
                    fileName: res.fileName
                })
            ),
            catchError(err => {
                this.loading$.next(false);
                this._toastService.error('Add Document failed');
                throw err;
            })
        );
    }
}
